<template>
  <VueDropzone
    id="dropZone"
    :ref="dropZoneKey"
    :options="dropzoneOptions"
    @vdropzone-success="onFileSuccess"
    @vdropzone-file-added="onFileAdded"
    :useCustomSlot="true"
    :include-styling="false"
  >
  <div class="ma-0 ml-1 mr-2 document-sync-component" style="display: flex">
      <v-row dense
          @dragenter="documentDragEnter"
          @dragleave="documentDragLeave"
          @drop="documentDragLeave"
          @dragover.prevent>
        <v-col cols="12" md="4">
          <v-card outlined tile class="rounded-xl" style="height: 95%">
            <div
              v-if="documentIsDragged"
              class="d-flex align-center justify-center"
              style="
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.4);
                z-index: 10;
                color: white;
                font-size: 18px;
                pointer-events: none;
              "
            >
              {{ $t("Site.DocumentRecognition.DragAndDrop") }} 📂
            </div>

            <v-overlay :value="loadingCheckTakeControl" absolute>
              <div class="text-center">
                <div>
                  <v-progress-circular
                    class="my-4"
                    :size="50"
                    width="5"
                    color="primary"
                    indeterminate
                  ></v-progress-circular>
                </div>
                <p class="display-1">{{ $t("CheckingControl") }}...</p>
              </div>
            </v-overlay>
            <div>
              <div class="d-flex align-center">
                <h4 class="pa-4 pr-0 pb-2 truncate flex" style="max-width: 50%">
                  {{ $t("DocumentProcessing") }}
                </h4>
                <AutoImportConfigsFailed class="mr-3 mt-1"></AutoImportConfigsFailed>
                <document-counter-component
                  class="mt-1 mr-2"
                  :refresh="refreshCounter"
                ></document-counter-component>
              </div>
              <div class="d-flex align-center ml-4 mr-4 mb-2 grey--text caption">
                {{ $t("LastUpdated") }}: {{ lastUpdated }}
              </div>
              <span class="d-flex ml-4 mr-4 mb-2 caption" v-if="connectionStatus"
                    :class="{
                        'grey--text lighten-1': (connectionStatus.type === 'info' || connectionStatus.type === 'success'),
                        'amber--text lighten-1': connectionStatus.type === 'warning',
                        'red--text lighten-1': connectionStatus.type === 'error',
                      }">
                {{ connectionStatus.message }}
              </span>
              <v-row dense class="mx-2 mb-2 pb-1">
                <v-col cols="12" class="py-1 px-2">
                  <v-btn
                    small
                    color="primary"
                    @click="forceAutoImport"
                    :loading="isLoadingForceAutoimport"
                    :disabled="isLoadingForceAutoimport"
                    class="col"
                  >
                    <template v-slot:loader>
                      <span class="custom-loader">
                        <v-icon light>mdi-cached</v-icon>
                      </span>
                    </template>
                    <v-icon class="me-2">mdi mdi-sync-circle</v-icon>
                    {{ $t("ForceSynchronization") }}
                  </v-btn>
                </v-col>
                <v-col cols="12" class="py-0">
                  <v-row class="ma-0">
                  <v-btn
                    style="width: 100%;"
                    class="col-6 ma-1"
                    small
                    color="red accent-4"
                    @click="deleteAllFailedDocument"
                  >
                    {{ $t("DeleteAllFailedDocuments") }}
                  </v-btn>
                  <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      style="width: 100%;"
                      class="col ma-1"
                      small
                      color="orange accent-4"
                      @click="retryAllFailedDocuments"
                    >
                      {{ $t("Retry") }}
                    </v-btn>
                  </template>
                  <span>{{ $t("RetryAllFailed") }}</span>
                </v-tooltip>
                </v-row>
                </v-col>
              </v-row>
            </div>
            <v-row justify="center" align="center" v-if="loading">
              <div class="text-center">
                <div>
                  <v-progress-circular
                    class="my-4"
                    :size="50"
                    width="5"
                    color="primary"
                    indeterminate
                  ></v-progress-circular>
                </div>
                <p class="display-1">{{ $t("Loading") }}...</p>
              </div>
            </v-row>
            <div style="max-height: calc(75vh - 100px); overflow-y: auto; overflow-x: hidden;">
              <v-container class="pa-0">
                <v-simple-table dense height="calc(75vh - 100px)" v-if="!loading">
                    <draggable
                      tag="tbody"
                      :options="{
                        sort: false,
                      }"
                      :list="synchronizedDocuments"
                      @start="onDocumentDragStart"
                      @end="onDocumentDragEnd"
                      ghostClass="selected-document_tocombine"
                      draggable=".draggableDocument"
                    >
                    <DocumentRecognitionItem
                          :ref="`documentItem-${item.messageId}`"
                          v-for="(item, index) in synchronizedDocuments"
                          :id="item.messageId"
                          :key="index"
                          :index="index"
                          @on-document-click="handleDocumentSelection"
                          @on-document-deleted="documentHasBeenDeleted"
                          :document-item="item"
                          :class="{
                              'selected-row':
                                selectedDocument && selectedDocument.messageId === item.messageId && (item.status === recognitionStatus.Failed || item.status === recognitionStatus.PendingReview || item.status === recognitionStatus.ContentTypeRecognitionFailed),
                              'cursor-pointer':
                                item.status === recognitionStatus.Failed || item.status === recognitionStatus.PendingReview || item.status === recognitionStatus.ContentTypeRecognitionFailed,
                              'no-hover':
                                item.status !== recognitionStatus.Failed && item.status !== recognitionStatus.PendingReview && item.status !== recognitionStatus.ContentTypeRecognitionFailed,
                                  'highlighted-document-item_combine': draggingDocument && documentBeingDragged.messageId !== item.messageId,
                                  'selected-document_dropzone': isValidZoneForDraggedDocument(item),
                                  'draggableDocument': item.status === recognitionStatus.PendingReview
                            }"
                    >
                    </DocumentRecognitionItem>
                    </draggable>
                </v-simple-table>
              </v-container>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined tile>
            <PdfViewer
              v-show="showViewer"
              :ref="viewerKey"
              :hideDownload="false"
            />
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <DocumentPendingReviewSection :selected-review-item="selectedDocument"
                                        @user-began-editing-a-document="setControlTakenOptionForMessage"
                                        @changes-have-been-saved="changesHaveBeenSaved"
                                        @document-edited-without-save="changeDocumentEditedWithoutSave"
                                        @document-requeued="documentRequeued">
          </DocumentPendingReviewSection>
        </v-col>
      </v-row>
    <v-snackbar
      top
      center
      color="success"
      :timeout="6000"
      v-model="showDocumentCreatedToast"
    >
      {{ $t("DocumentCreatedSuccessfully") }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="viewCreatedDocuments">
          {{ $t("ViewDocuments") }}
        </v-btn>
        <v-btn
          dark
          v-bind="attrs"
          @click.native="showDocumentCreatedToast = false"
          icon
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <MergePendingRecognitionDocumentModal
      :ref="mergeDocumentModalKey"
      @on-merge-documents="reloadDocument"
    ></MergePendingRecognitionDocumentModal>
    <documents-dragged-toast
      :showDocumentsDraggedToast="showDocumentsDraggedToast"
      :documentsCount="draggedDocuments.length"
      :draggedDocumentsFailed="draggedDocumentsFailed"
      @close="onDraggedDocumentsToastClose"
      @remove-item="removeFailedDocument"
      />
  </div>
  </VueDropzone>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import DocumentViewer from '@/components/search/results/DocumentViewer';
import PdfViewer from '@/components/document/PdfViewer';
import DocumentIntelligenceProvidersService from '@/services/DocumentIntelligenceProvidersService';
import AttachmentList from '@/components/document/documentModal/attachment/AttachmentList';
import Utils from '@/ecm/common/Utils';
import DocumentService from '@/services/DocumentService';
import DocumentField from '@/components/document/documentModal/field/DocumentField';
import { EventBus } from '@/eventBus.js';
import RecognitionStatus from '@/ecm/variables/RecognitionStatus.js';
import 'splitpanes/dist/splitpanes.css';
import AutoImportService from '../../services/AutoImportService';
import DocumentRecognitionItem from '@/components/aiRecognition/Components/DocumentRecognitionItem.vue';
import DocumentCounterComponent from '@/components/aiRecognition/Components/DocumentCounterComponent.vue';
import DocumentPendingReviewSection from '@/components/aiRecognition/Components/DocumentPendingReviewSection.vue';
import SearchType from '@/ecm/variables/SearchType';
import MergePendingRecognitionDocumentModal from '@/components/aiRecognition/Components/MergePendingRecognitionDocumentModal.vue';
import AutoImportConfigsFailed from '@/components/aiRecognition/Components/AutoImportConfigsFailed.vue';
import DocumentsDraggedToast from '@/components/aiRecognition/Components/DocumentsDraggedToast.vue';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import { createLegacyHubConnection } from '@/plugins/signalr';

export default {
    async beforeRouteLeave(to, from, next) {
        if (to.fullPath !== '/login' && this.documentEditedWithoutSave === true) {
            try {
                const res = await this.unsavedChangesMessageConfirm();
                switch (res) {
                case 2:
                    return;
                case 1:
                    this.loading = true;
                    await DocumentIntelligenceProvidersService.deleteLockedDocument({ messageId: this.selectedDocument.messageId, originalFileName: this.selectedDocument.originalFileName });
                    this.loading = false;
                    next();
                    break;
                }
            } catch (error) {
                console.error('Error during save:', error);
                next(false);
            }
        } else {
            next();
        }
    },
    data() {
        const url = `${this.$store.getters['account/apiUrl']}/api/document-intelligence/recognize-dragged-document`;

        return {
            connection: null,
            connectionStatus: null,
            signalRIsTryingToReconnect: false,
            synchronizedDocuments: [],
            selectedDocument: null,
            documentViewerKey: 'DocumentViewer',
            mergeDocumentModalKey: 'mergeDocumentModal',
            documentListKey: 'DocumentList',
            viewerKey: 'documentListPdfViewer',
            recognitionStatus: RecognitionStatus,
            showViewer: false,
            currentUser: this.$store.getters['account/user'],
            apiUrl: this.$store.getters['account/apiUrl'],
            token: this.$store.getters['account/token'],
            lastUpdated: '',
            loading: false,
            isLoadingForceAutoimport: false,
            selectedPreviousDocumentIndex: -1,
            documentEditedWithoutSave: false,
            loadingCheckTakeControl: false,
            refreshCounter: 0,
            createdDocumentsIds: [],
            showDocumentCreatedToast: false,
            documentBeingDragged: null,
            draggingDocument: false,
            dragDropDocumentId: null,
            dropZoneKey: 'drop-zone',
            dropzoneOptions: {
                clickable: false,
                previewsContainer: false,
                uploadMultiple: false,
                url: url,
                timeout: 60000, //30 seconds
                maxFiles: 20,
                maxFilesize: 1000, //1 GB
                destroyDropzone: true,
                addRemoveLinks: true,
                acceptedFiles: '',
                headers: {
                    Authorization:
                        'bearer ' + this.$store.getters['account/token'],
                },
            },
            documentIsDragged: false,
            showDocumentsDraggedToast: false,
            draggedDocuments: [],
            draggedDocumentsFailed: [],
            dragCounter: 0,
        };
    },
    mixins: [validationMixin],
    validations: {
        contentTypeId: { required },
        document: {
            contentType: { required },
            fields: {
                $each: {
                    value: {
                        required: requiredIf(function (model) {
                            return model.isRequired;
                        }),
                    },
                },
            },
        },
    },
    components: {
        DocumentRecognitionItem,
        DocumentPendingReviewSection,
        DocumentViewer,
        PdfViewer,
        AttachmentList,
        DocumentField,
        RecognitionStatus,
        DocumentCounterComponent,
        MergePendingRecognitionDocumentModal,
        AutoImportConfigsFailed,
        DocumentsDraggedToast,
        VueDropzone: vue2Dropzone
    },
    props: {
        filePath: String,
    },
    computed: {
        viewerInstance() {
            return this.$refs[this.viewerKey];
        }
    },
    methods: {
        addDragEventListeners() {
            this.removeDragEventListeners();
            this.$nextTick(() => {
                this.synchronizedDocuments.forEach((item) => {
                    const element = this.$refs[`documentItem-${item.messageId}`];
                    if (element[0] && element[0].$el) {
                        element[0].$el.addEventListener('dragenter', this.onDragEnter);
                    }
                });
            });
        },
        removeDragEventListeners() {
            const draggableDocuments = document.querySelectorAll('.draggableDocument');
            draggableDocuments.forEach((item) => {
                item.removeEventListener('dragenter', this.onDragEnter);
            });
        },
        onDragEnter(event) {
            event.preventDefault();
            const trElement = event.target.closest('tr');
            const targetItem = this.synchronizedDocuments.find(
                (doc) => doc.messageId === trElement.id
            );
            this.dragDropDocumentId = targetItem.messageId;
        },
        isValidZoneForDraggedDocument(doc) {
            return (this.draggingDocument && this.documentBeingDragged.messageId !== doc.messageId) &&
                (this.dragDropDocumentId === doc.messageId) && (doc.status === RecognitionStatus.PendingReview);
        },
        reloadDocument(document) {
            this.updateRefreshCounter();
            this.selectedDocument = this.synchronizedDocuments.find((item) => item.messageId === document.messageId);
            this.loadDocumentFields(document);
            this.viewerInstance.reload();
        },
        onDocumentDragStart(event) {
            const draggedDocument = this.synchronizedDocuments.find(
                (item) => item.messageId == event.item.id
            );
            if (!draggedDocument || draggedDocument.status !== RecognitionStatus.PendingReview) {
                return;
            }
            if (draggedDocument.controlTakenByUserID !== null && this.currentUser.Id !== draggedDocument.controlTakenByUserID) {
                Utils.toast.error(this.$t('DocumentAlreadyTaken').replace('{0}', draggedDocument.originalFileName));
                return;
            }
            this.draggingDocument = true;
            this.documentBeingDragged = draggedDocument;
        },
        onDocumentDragEnd(event) {
            this.dragDropDocumentId = null;
            this.draggingDocument = false;
            const dragDropX = event.originalEvent.clientX;
            const dragDropY = event.originalEvent.clientY;
            const elementAtPoint = document.elementFromPoint(dragDropX, dragDropY);
            const documentToMergeWithRow = elementAtPoint.closest('.draggableDocument');
            if (documentToMergeWithRow) {
                const documentToMergeWith = this.synchronizedDocuments.find(
                    (item) => item.messageId == documentToMergeWithRow.id
                );
                if (!documentToMergeWith || documentToMergeWith.status !== RecognitionStatus.PendingReview) {
                    return;
                }
                if ((documentToMergeWith.controlTakenByUserID !== null && this.currentUser.Id !== documentToMergeWith.controlTakenByUserID)) {
                    Utils.toast.error(this.$t('DocumentAlreadyTaken').replace('{0}', documentToMergeWith.originalFileName));
                    return;
                }
                if (!this.documentBeingDragged || this.documentBeingDragged.messageId === documentToMergeWith.messageId) {
                    return;
                }
                this.mergeDocuments(this.documentBeingDragged, documentToMergeWith);
            }
            this.documentBeingDragged = null;
        },
        mergeDocuments(documentBeingDragged, documentToMergeWith) {
            this.$refs[this.mergeDocumentModalKey].show(documentBeingDragged, documentToMergeWith);
        },
        manageSignalR() {
            this.connection = createLegacyHubConnection(this.apiUrl, this.token);
            const notificationHub = this.connection.createHubProxy('notificationHub');
            const self = this;

            notificationHub.on('refresh-queues', (refreshedDocument) => {
                self.setUpdatedObject(refreshedDocument);
                this.updateLastUpdated();
            });

            notificationHub.on('user-took-control-of-document', (takeControlResponse) => {
                if (takeControlResponse.PreviousDocumentTakenByCurrentUser) {
                    const objLostControl = self.synchronizedDocuments.find(x => x.messageId === takeControlResponse.PreviousDocumentTakenByCurrentUser);

                    if (objLostControl)
                        objLostControl.controlTakenByUserID = null;
                }

                if (takeControlResponse.UserID !== this.currentUser.Id) {
                    self.removeSelectedDocument(takeControlResponse.MessageID);
                    this.documentEditedWithoutSave = false;
                }

                const obj = self.synchronizedDocuments.find(x => x.messageId === takeControlResponse.MessageID);

                this.setControlTakenOptionForMessage(obj, takeControlResponse.UserID);
                this.updateLastUpdated();
            });

            notificationHub.on('user-lost-control-of-document', (lostControlResponse) => {
                const obj = self.synchronizedDocuments.find(x => x.messageId === lostControlResponse);
                obj.controlTakenByUserID = null;
                this.updateLastUpdated();
            });

            EventBus.$on('connection-status', (message) => {
                this.connectionStatus = message;
            });

            // Handle SignalR events for connection status
            this.connection.connectionSlow(() => {
                EventBus.$emit('connection-status', { message: self.$t('SignalRInSlowMode'), type: 'warning' });
            });

            this.connection.reconnecting(() => {
                EventBus.$emit('connection-status', { message: self.$t('SignalRTryingToReconnect'), type: 'warning' });
            });

            this.connection.reconnected(() => {
                EventBus.$emit('connection-status', null);
            });

            this.connection.disconnected(() => {
                EventBus.$emit('connection-status', { message: self.$t('SignalRDisconnected'), type: 'error' });
                // Attempt to reconnect
                self.tryReconnect();
            });

            this.connection.error(() => {
                EventBus.$emit('connection-status', { message: self.$t('SignalRError'), type: 'error' });
            });

            this.connection.start().done(() => {
            }).fail(() => {
                EventBus.$emit('connection-status', { message: self.$t('SignalRCouldNotReconnect'), type: 'error' });
            });
        },

        changesHaveBeenSaved(messageId, documentIds) {
            this.createdDocumentsIds = documentIds;
            this.showDocumentCreatedToast = true;
            this.synchronizedDocuments = this.synchronizedDocuments.filter((doc) => doc.messageId !== messageId);

            this.documentEditedWithoutSave = false;
            this.updateRefreshCounter();
            this.openFirstDocument();
        },

        changeDocumentEditedWithoutSave(newValue) {
            this.documentEditedWithoutSave = newValue;
        },

        documentRequeued() {
            this.documentEditedWithoutSave = false;
            this.updateLastUpdated();
            this.updateRefreshCounter();
            this.openFirstDocument();
        },

        viewCreatedDocuments() {
            const pattern = {
                searchPattern: this.createdDocumentsIds.join(),
                searchAllVersions: false,
            };

            const route = `/search/${SearchType.DocumentIDs}/${encodeURIComponent(JSON.stringify(pattern))}`;

            this.showDocumentCreatedToast = false;
            this.$router.push(route);
        },

        documentHasBeenDeleted(messageId, actionExecutedByOtherUser) {
            this.documentEditedWithoutSave = false;

            this.synchronizedDocuments = this.synchronizedDocuments.filter((doc) => doc.messageId !== messageId);

            if (!actionExecutedByOtherUser) {
                this.openFirstDocument();
            }

            if (this.synchronizedDocuments.length === 0) {
                this.resetSelectedDocument();
            }

            this.updateRefreshCounter();
        },

        tryReconnect() {
            if (this.signalRIsTryingToReconnect)
                return;

            let attempts = 0;
            const maxAttempts = 5;
            const self = this;
            this.signalRIsTryingToReconnect = true;

            function reconnect() {
                if (attempts < maxAttempts) {
                    self.connection.start().done(() => {
                        self.signalRIsTryingToReconnect = false;
                        EventBus.$emit('connection-status', null);
                    }).fail(() => {
                        attempts++;
                        setTimeout(reconnect, 5000); // Retry after 5 seconds
                    });
                } else {
                    EventBus.$emit('connection-status', { message: self.$t('SignalRCouldNotReconnect'), type: 'error' });
                }
            }

            reconnect();
        },

        deleteAllFailedDocument() {
            this.$confirm(this.$t('DeleteAllFailedDocumentsExceptBlocked'), {
                title: this.$t('DeleteAllFailedDocuments'),
                icon: 'mdi-warning',
                color: 'orange',
                buttonTrueText: this.$t('Ok'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res === 1) {
                    DocumentIntelligenceProvidersService.removeAllFailedDocuments().then(
                        (response) => {
                            if (response) {
                                Utils.toast.success(this.$t('DocumentsDeletedSuccessfully'));
                                this.synchronizedDocuments = this.synchronizedDocuments.filter(x => x.status !== RecognitionStatus.Failed || x.ControlTakenByUserID !== null);
                                this.updateRefreshCounter();
                                this.openFirstDocument();
                            } else {
                                Utils.toast.error(this.$t('FailedToDeleteDocuments'));
                            }
                        }
                    );
                }
            });
        },

        openFirstDocument() {
            this.documentEditedWithoutSave = false;
            this.resetSelectedDocument();

            for (let i = 0; i < this.synchronizedDocuments.length; i++) {
                const item = this.synchronizedDocuments[i];
                if (item.status === RecognitionStatus.PendingReview || item.status === RecognitionStatus.Failed || item.status === RecognitionStatus.ContentTypeRecognitionFailed) {
                    this.handleDocumentSelection(item, i);
                    break;
                }
            }
        },

        resetSelectedDocument() {
            this.selectedDocument = null;
            this.showViewer = false;
        },

        async unsavedChangesMessageConfirm() {
            return new Promise((resolve) => {
                this.$confirm(this.$t('UnsavedChanges'), {
                    title: this.$t('PendingChanges'),
                    icon: 'mdi-warning',
                    color: 'orange',
                    width: 400,
                    buttonTrueText: this.$t('DiscardChanges'),
                    buttonTrueColor: 'grey',
                    buttonFalseText: this.$t('Cancel'),
                }).then((res) => {
                    resolve(res);
                });
            });
        },

        async handleDocumentSelection(document, index) {
            //Only allow to open the document if the status is pending review or failed
            if (document.status !== RecognitionStatus.PendingReview && document.status !== RecognitionStatus.Failed && document.status !== RecognitionStatus.ContentTypeRecognitionFailed) {
                return;
            }
            if (this.documentEditedWithoutSave && this.selectedPreviousDocumentIndex !== index) {

                // Check that the control is not taken before you are asked if you want to discard the changes.
                this.loadingCheckTakeControl = true;
                const result = await DocumentIntelligenceProvidersService.checkDocumentAvailability(this.selectedDocument.messageId);
                this.loadingCheckTakeControl = false;

                // The document is already being edited
                if (this.selectedDocument.messageId === document.messageId) {
                    return;
                }

                // Nobody is editing, or another user.
                if (!result || result.UserID !== this.currentUser.Id) {
                    this.loadDocumentFields(document);
                    return;
                }

                const optionResult = await this.unsavedChangesMessageConfirm();

                switch (optionResult) {
                case 2:
                    return;
                case 1:
                    this.loadingCheckTakeControl = true;
                    DocumentIntelligenceProvidersService.deleteLockedDocument({ messageId: this.selectedDocument.messageId, originalFileName: this.selectedDocument.originalFileName })
                        .then(() => {
                            this.loadingCheckTakeControl = false;
                            this.setControlTakenOptionForMessage(this.selectedDocument, null);
                            this.loadDocumentFields(document);
                            this.documentEditedWithoutSave = false;
                        })
                        .catch(() => {
                            this.loadingCheckTakeControl = false;
                        });
                    break;
                }

            } else {
                this.loadDocumentFields(document);
            }
        },

        loadDocumentFields(document) {
            this.selectedDocument = document;
            this.documentEditedWithoutSave = false;

            this.loadFile(document);
        },

        setControlTakenOptionForMessage(document, userId) {
            this.synchronizedDocuments.forEach(doc => {
                if (doc.controlTakenByUserID && doc.controlTakenByUserID === userId && doc.messageId !== document.messageId)
                    doc.controlTakenByUserID = null;
            });

            document.controlTakenByUserID = userId == null ? null : userId;
        },

        loadFile(item) {
            DocumentService.displayTempPdf(item.messageId, item.filePath.split('.').pop(), 'PendingObjects').then((url) => {
                this.viewerInstance.load(encodeURIComponent(url), 0);
                this.showViewer = true;
            });
        },

        getFormattedDate(importedDate) {
            return Utils.date.toUserFormatFromIso(importedDate);
        },

        getQueues() {
            this.loading = true;

            DocumentIntelligenceProvidersService.getQueuesMessages().then(
                (response) => {
                    this.synchronizedDocuments = response.sort(
                        (a, b) => new Date(b.importDate) - new Date(a.importDate)
                    );
                    this.updateLastUpdated();
                    this.getAllLockedDocuments();
                    this.openFirstDocument();
                    this.addDragEventListeners();
                    this.loading = false;
                }
            );
        },

        updateOrAddDocument(pendingObject) {
            const existingIndex = this.synchronizedDocuments.findIndex((doc) => doc.messageId === pendingObject.messageId);

            if (existingIndex !== -1) {
                this.synchronizedDocuments[existingIndex] = pendingObject;
            } else {
                this.synchronizedDocuments.push(pendingObject);
            }
        },

        setUpdatedObject(pendingObject) {
            switch (pendingObject.status) {
            case RecognitionStatus.PendingRecognizing:
            case RecognitionStatus.OcrProcess:
            case RecognitionStatus.InRecognitionQueue:
            case RecognitionStatus.BeingRecognised:
            case RecognitionStatus.Recognized:
            case RecognitionStatus.NotRecognized:
            case RecognitionStatus.Failed:
            case RecognitionStatus.PendingReview:
            case RecognitionStatus.InContentTypeRecognition:
            case RecognitionStatus.ContentTypeRecognitionFailed:
                this.updateOrAddDocument(pendingObject);
                break;
            case RecognitionStatus.Deleted:
                this.documentHasBeenDeleted(pendingObject.messageId, true);
                break;
            case RecognitionStatus.Validated:
                this.updateOrAddDocument(pendingObject);
                setTimeout(() => {
                    this.synchronizedDocuments = this.synchronizedDocuments.filter((doc) => doc.messageId !== pendingObject.messageId);
                }, 5000);
                break;
            case RecognitionStatus.DiscardedAfterSplit:
                this.updateOrAddDocument(pendingObject);
                setTimeout(() => {
                    this.synchronizedDocuments = this.synchronizedDocuments.filter((doc) => doc.messageId !== pendingObject.messageId);
                }, 2000);
                break;
            }

            this.synchronizedDocuments = this.synchronizedDocuments
                .sort((a, b) => new Date(b.importDate) - new Date(a.importDate));

            this.updateRefreshCounter();

            if (this.selectedDocument) {
                const updatedDocument = this.synchronizedDocuments.find(
                    (doc) => doc.messageId === this.selectedDocument.messageId
                );

                if (updatedDocument) {
                    this.selectedDocument = updatedDocument;
                } else {
                    this.selectedDocument = this.synchronizedDocuments.length > 0
                        ? this.synchronizedDocuments[0]
                        : null;
                }
            }

            this.getAllLockedDocuments();
        },
        updateLastUpdated() {
            const nowIso = new Date().toISOString();
            this.lastUpdated = this.getFormattedDate(nowIso);
        },
        forceAutoImport() {
            if (this.isLoadingForceAutoimport) return;

            this.isLoadingForceAutoimport = true;

            AutoImportService.forceAutoImportJob()
                .then(async (response) => {
                    Utils.toast.success(this.$t('DocSynchronizationSuccess'));
                    this.addDragEventListeners();
                    await this.getAllLockedDocuments();
                })
                .catch(() => {
                    Utils.toast.error(this.$t('DocSynchronizationError'));
                })
                .finally(() => {
                    this.isLoadingForceAutoimport = false;
                });
        },
        getAllLockedDocuments() {
            DocumentIntelligenceProvidersService.getAllAutoImportDocumentsLocked().then((response) => {
                this.synchronizedDocuments.forEach(doc => {
                    const lockedDocument = response.find(lockedDoc => lockedDoc.MessageID === doc.messageId);

                    if (lockedDocument) {
                        this.setControlTakenOptionForMessage(doc, lockedDocument.UserID);
                    }
                });
            });
        },
        removeSelectedDocument(messageId) {
            if (this.selectedDocument && this.selectedDocument.messageId === messageId) {
                this.selectedDocument = null;
                this.showViewer = false;
            }
        },
        retryAllFailedDocuments() {
            const failedDocuments = this.synchronizedDocuments.filter(x => x.status === RecognitionStatus.Failed || x.status === RecognitionStatus.ContentTypeRecognitionFailed);
            if (failedDocuments.length !== 0) {
                const retryFailedRequest = {
                    PendingObjects: failedDocuments
                };

                DocumentIntelligenceProvidersService.sendPendingFailedObjectsToRequeue(retryFailedRequest).then((response) => {
                    if (response && response > 0) {
                        Utils.toast.success(response + ' ' + this.$t('DocumentsSuccesfullyRequeued'));
                        this.updateLastUpdated();
                        this.updateRefreshCounter();
                    } else {
                        Utils.toast.error(this.$t('ErrorRetryingDocuments'));
                    }
                });
            }
        },
        updateRefreshCounter() {
            const today = new Date();
            today.setMilliseconds(0);
            this.refreshCounter = today.getTime();
        },
        documentDragLeave(e) {
            e.preventDefault();
            this.dragCounter--;
            if (this.dragCounter <= 0) {
                this.documentIsDragged = false;
                this.dragCounter = 0;
            }
        },
        documentDragEnter(e) {
            e.preventDefault();
            this.dragCounter++;
            this.documentIsDragged = true;
        },
        onFileAdded(file) {
            const maxFileSizeMB = this.dropzoneOptions.maxFilesize;
            const fileSizeMB = file.size / 1024 / 1024;

            if (fileSizeMB > maxFileSizeMB) {
                file.errorMessage = this.$t('UnSuportedFileSize');
                this.draggedDocumentsFailed.push(file);
                return;
            }

            const extension = file.name.split('.').pop();
            const unSuportedFile = this.dropzoneOptions.acceptedFiles
                .split(',')
                .filter((f) => f.toUpperCase() == `.${extension}`.toUpperCase()
                ).length == 0;

            if (unSuportedFile) {
                file.errorMessage = this.$t('DropzoneInvalidFileType');
                this.draggedDocumentsFailed.push(file);
                return;
            }

            this.draggedDocuments.push(file);
            this.showDocumentsDraggedToast = true;
        },
        onFileSuccess(file, response) {
            this.draggedDocuments = this.draggedDocuments.filter(x => x.name !== response);
            if (this.draggedDocuments.length === 0 && this.draggedDocumentsFailed.length === 0) {
                this.showDocumentsDraggedToast = false;
            }
        },
        onDraggedDocumentsToastClose() {
            this.draggedDocuments = [];
            this.draggedDocumentsFailed = [];
        },
        removeFailedDocument(itemId) {
            this.draggedDocumentsFailed = this.draggedDocumentsFailed.filter(x => x.upload.uuid !== itemId);
        },
    },
    mounted() {
        this.updateRefreshCounter();
        this.getQueues();
        this.manageSignalR();
        DocumentService.getSupportedFileFormats().then((data) => {
            this.dropzoneOptions.acceptedFiles = data.join(',');
        });
    },
    beforeDestroy() {
        this.removeDragEventListeners();
    },
    destroyed() {
        if (this.connection) {
            $(this.connection).unbind('onReconnecting', null);
            $(this.connection).unbind('onReconnect', null);
            $(this.connection).unbind('onDisconnect', null);
            $(this.connection).unbind('onError', null);
            $(this.connection).unbind('onConnectionSlow', null);
            this.connection.stop();

            this.connection = null;
        }
    }
};
</script>
<style>

.document-sync-component .no-hover:hover {
  background: transparent !important;
}
.document-sync-component .loading-icon {
  animation: spin 5s linear infinite;
  font-size: small !important;
}
.document-sync-component tbody tr.selected-row {
  background-color: #d1cece83 !important;
}
.document-sync-component .cursor-pointer {
  cursor: pointer;
}
.document-sync-component .provider-width {
  max-width: 7vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.document-sync-component .fileName-width {
  max-width: 16vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.document-sync-component .truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;
}
.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}
.document-sync-component .triangle-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.document-sync-component .triangle {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  border-top: 2.3rem solid ;
  border-right: 2.3rem solid transparent;
}

.document-sync-component .icon-container {
  position: absolute;
  top: -2.4rem;
  right: -1.1rem;
}
@-moz-keyframes loader {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0);
  }
}
@keyframes loader {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0);
  }
}
.selected-document_tocombine {
  background-color: #dbffdb !important;
}
.selected-document_dropzone {
  background-color: #e2f8ff !important;
}

</style>
